import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import cn from 'classnames';
import PropTypes from 'prop-types';
import FuseConfirmationModal from '../ConfirmationModal/FuseConfirmationModal';
import FuseConsoleModal from '../ConsoleModal/FuseConsoleModal';
import FuseButton from '../FuseControls/FuseButton';
import { Card, FTBody, FTLabel } from '../Fuse';
import FuseTextfield from '../FuseControls/FuseTextfield';
import { ObservableFuseSelect } from '../FuseControls/FuseSelect';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseCheckbox from '../FuseControls/FuseCheckbox';
import AdministratorsStore from '../../stores/Administrators/AdministratorsStore';
import { fetchDisplayRoleName, ModalMode } from '../../stores/Administrators/AdministratorModalStore';
import SnackStore from '../../stores/SnackStore';
import './AdministratorModal.css';

const block = b.lock('AdministratorModal');

class AdministratorModal extends Component {
    static propTypes = {
        store: AdministratorsStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        subtitle: PropTypes.string,
        sendInvitation: PropTypes.bool,
        readOnly: PropTypes.bool,
    };

    onModalOpen = () => {
        const { userModal } = this.props.store;
        if (userModal.mode === ModalMode.EDIT) {
            userModal.email.focus();
        }
    };

    onCreateClick = () => {
        const { snack, store } = this.props;
        const { userModal, createUser } = store;
        createUser().then(() => snack.showSuccess(`Administrator ${userModal.email.value} have been created.`));
    };

    onDeleteClick = () => {
        const { snack, store } = this.props;
        const { userModal, deleteUser } = store;
        deleteUser().then(() => snack.showSuccess(`Administrator ${userModal.email.value} have been deleted.`));
    };

    onSaveClick = () => {
        const { snack, store } = this.props;
        const { userModal, updateUser } = store;
        updateUser().then(() => snack.showSuccess(`Edits to administrator ${userModal.email.value} have been saved.`));
    };

    onModalClose = () => {
        const { userModal, exitConfirmationModal } = this.props.store;
        if (userModal.hasChanged()) {
            exitConfirmationModal.open();
        } else {
            userModal.modal.close();
        }
    };

    getEmailErrorMessage = () => {
        const { userModal, updateStatus } = this.props.store;
        if (!userModal.email.isTouched) {
            if (updateStatus.isFailed) {
                return updateStatus.error.message;
            }
            if (!userModal.email.isValid) {
                return 'Email is invalid';
            }
        }
    };

    renderHeaderAction = () => {
        const { readOnly, store } = this.props;
        const { userModal, updateStatus } = store;
        const { mode, hasChanged, setMode, deleteConfirmation } = userModal;

        if (mode === ModalMode.EDIT || mode === ModalMode.CREATE) {
            return <FuseButton
                style={{ minWidth: 100 }}
                color={'green'}
                primary
                disabled={!hasChanged()}
                loading={updateStatus.isLoading}
                onClick={mode === ModalMode.EDIT ? this.onSaveClick : this.onCreateClick}
            >Save</FuseButton>;
        }

        if (mode === ModalMode.VIEW && !readOnly) {
            return <FuseButton
                style={{ minWidth: 100 }}
                color={'blue'}
                primary
                icon={'edit'}
                onClick={() => setMode(ModalMode.EDIT)}
                disabled={!userModal.canEdit}
            >Edit</FuseButton>;
        }

        if (mode === ModalMode.DELETE) {
            return <FuseButton
                style={{ minWidth: 100 }}
                color={'red'}
                primary
                disabled={!deleteConfirmation.isTrue}
                loading={updateStatus.isLoading}
                onClick={this.onDeleteClick}
            >Delete</FuseButton>;
        }
    };

    renderViewMode = () => {
        const { userModal } = this.props.store;

        return <Fragment>
            <div className={cn(block('TextField'), block('ReadOnlyTextField'))}>
                <FTLabel className={block('ReadOnlyTextFieldLabel')}>Email</FTLabel>
                <FTBody>{userModal.email.value}</FTBody>
            </div>

            <div className={cn(block('TextField'), block('ReadOnlyTextField'))}>
                <FTLabel className={block('ReadOnlyTextFieldLabel')}>First Name</FTLabel>
                <FTBody>{userModal.firstName.value || '--'}</FTBody>
            </div>

            <div className={cn(block('TextField'), block('ReadOnlyTextField'))}>
                <FTLabel className={block('ReadOnlyTextFieldLabel')}>Last Name</FTLabel>
                <FTBody>{userModal.lastName.value || '--'}</FTBody>
            </div>

            <div className={cn(block('TextField'), block('ReadOnlyTextField'))}>
                <FTLabel className={block('ReadOnlyTextFieldLabel')}>Role</FTLabel>
                <FTBody>{fetchDisplayRoleName(userModal.roleSelect.selectedValues[0]) || ''}</FTBody>
            </div>
        </Fragment>;
    };

    renderEditMode = () => {
        const { store, sendInvitation } = this.props;
        const { userModal } = store;
        const emailErrorMessage = this.getEmailErrorMessage();

        return <Fragment>
            <FuseTextfield
                className={block('TextField')}
                label="Email Address"
                placeholder="Enter email address"
                store={userModal.email}
                showRequired
                error={!!emailErrorMessage}
                bottomText={emailErrorMessage}
                required
            />

            <FuseTextfield
                className={block('TextField')}
                label="First Name"
                placeholder="Enter first name"
                store={userModal.firstName}
            />

            <FuseTextfield
                className={block('TextField')}
                label="Last Name"
                placeholder="Enter last name"
                store={userModal.lastName}
            />

            <ObservableFuseSelect
                containerClassName={block('TextField', { selectField: true })}
                hintClassName={block('SelectHint')}
                selectLabel="Role"
                store={userModal.roleSelect}
                singleSelection
            />

            {sendInvitation && userModal.mode === ModalMode.CREATE ? <FuseCheckbox
                className={block('SendInvitationCheckbox')}
                store={userModal.sendInvitation}
                label="Send invitation to email address"
            /> : null}
        </Fragment>;
    };

    render() {
        const { store, subtitle } = this.props;
        const { userModal } = store;

        return <Fragment>
            <FuseConsoleModal
                isOpen={userModal.modal.isOpen}
                onClose={this.onModalClose}
                onAfterOpen={this.onModalOpen}
                title={
                    (userModal.mode === ModalMode.CREATE ? 'Add ' : '') +
                    (userModal.mode === ModalMode.EDIT ? 'Edit ' : '') +
                    (userModal.mode === ModalMode.DELETE ? 'Delete ' : '') +
                    'Administrator'
                }
                subTitle={' - ' + subtitle}
                className={block()}
                headerClassName={block('Header')}
                headerActions={this.renderHeaderAction()}
            >
                {userModal.mode === ModalMode.DELETE ? (
                    <FuseAlert
                        type="danger"
                        title="Delete Administrator Confirmation"
                        className={block('DeleteConfirmation')}
                    >
                        <FTBody className={block('DeleteModalAlertMessage')}>
                            After deleting this administrator they will no longer have access to Proofpoint
                            Isolation Console. They can be added back at any time.
                        </FTBody>
                        <FuseCheckbox
                            black
                            store={userModal.deleteConfirmation}
                            label="Confirm the deletion of this administrator"
                        />
                    </FuseAlert>
                ) : null}

                {userModal.mode === ModalMode.VIEW && !userModal.canEdit ? <FuseAlert
                    type="warning"
                    title="Primary admin editing is restricted"
                    style={{ marginBottom: 16 }}
                >
                    Please reach out to Proofpoint support to request a change of the primary admin if needed.
                </FuseAlert> : null}

                <Card title="Summary" className={block('Content')}>
                    {userModal.mode === ModalMode.CREATE || userModal.mode === ModalMode.EDIT ?
                        this.renderEditMode() : this.renderViewMode()}
                </Card>
            </FuseConsoleModal>

            <FuseConfirmationModal
                store={store.exitConfirmationModal}
                onConfirm={store.userModal.modal.close}
            />
        </Fragment>;
    }
}

export default inject('snack')(observer(AdministratorModal));
